import { match } from 'ts-pattern'
import type { ApplicationReference, Patent } from '../../schemas'

/**
 * Returns a formatted application number string for a patent.
 *
 * Formatting is jurisdiction-specific:
 * - For US: If the application number is 8 digits, it is formatted in the style of Google Patents (e.g., US12/345,678).
 *   Otherwise, it is prefixed with 'US' and left as-is.
 * - For WO: Ensures the 'PCT' prefix is present, as KTMine data can be inconsistent.
 * - For other jurisdictions: The application number is returned as-is, possibly with a jurisdiction prefix.
 *
 * This function does not compute or append checksum digits (such as those used in some
 * European or Japanese application numbers). If a checksum digit is present in the incoming data,
 * it will be displayed as part of the application number. If not, no checksum is added or calculated.
 */
export const getApplicationNumberFromReference = ({
  ktmineApplicationReferences = [],
  defaultBaseApplicationNumber,
  jurisdiction,
}: {
  ktmineApplicationReferences?: ApplicationReference[]
  defaultBaseApplicationNumber: Patent['applicationNumber']
  jurisdiction: Patent['jurisdiction']
}): string => {
  // We only use original format application numbers (no docdb, etc.)
  const originalApplicationNumber =
    ktmineApplicationReferences
      .filter(
        ({ dataFormat, documentNumber }) =>
          dataFormat === 'original' && !!documentNumber
      )
      .sort((a, b) => {
        // Prefer application numbers with checksums
        const doesAEndInChecksum = /[.-]\d$/.test(a.documentNumber ?? '')
        const doesBEndInChecksum = /[.-]\d$/.test(b.documentNumber ?? '')

        if (doesAEndInChecksum && !doesBEndInChecksum) {
          return -1
        }
        if (!doesAEndInChecksum && doesBEndInChecksum) {
          return 1
        }
        return 0
      })[0]?.documentNumber ?? defaultBaseApplicationNumber

  return (
    match(jurisdiction)
      .with('US', () => {
        if (
          defaultBaseApplicationNumber.length !== 8 ||
          !/^\d{8}$/.test(defaultBaseApplicationNumber)
        ) {
          return `US${defaultBaseApplicationNumber}`
        }

        // Format it like Google Patents (e.g., US12/345,678)
        const series = defaultBaseApplicationNumber.slice(0, 2)
        const serial = defaultBaseApplicationNumber.slice(2)
        const formattedSerial = `${serial.slice(0, 3)},${serial.slice(3)}`
        return `US${series}/${formattedSerial}`
      })
      .with('WO', () => {
        // KTMine is inconsistent with the PCT prefix, so we need to fix application numbers missing the prefix
        const applicationNumbers = ktmineApplicationReferences
          .filter(
            ({ dataFormat, documentNumber }) =>
              dataFormat === 'original' && !!documentNumber
          )
          .sort((a, b) => {
            const doesAStartWithPCT = a.documentNumber?.startsWith('PCT')
            const doesBStartWithPCT = b.documentNumber?.startsWith('PCT')
            if (doesAStartWithPCT && !doesBStartWithPCT) {
              return -1
            }
            if (!doesAStartWithPCT && doesBStartWithPCT) {
              return 1
            }
            return 0
          })

        const firstApplicationNumber = applicationNumbers[0]?.documentNumber

        const shouldUsePCTPrefix =
          firstApplicationNumber != null &&
          !firstApplicationNumber.startsWith('PCT') &&
          /^[A-Z]{2}\d{4}\/.*/.test(firstApplicationNumber)

        if (firstApplicationNumber) {
          return `${shouldUsePCTPrefix ? 'PCT/' : ''}${firstApplicationNumber}`
        }

        return defaultBaseApplicationNumber
      })
      // Jurisdictions that require a kind
      .with(
        'AR',
        'AU',
        'BR',
        'CA',
        'CH',
        'CN',
        'DE',
        'EP',
        'ES',
        'FR',
        'GB',
        'IL',
        'IT',
        'JP',
        (jurisdiction) => {
          // KTMine is inconsistent with which formats have a kind, so we grab the first available kind
          const kind =
            ktmineApplicationReferences.find(({ kind }) => !!kind)?.kind ?? ''

          return `${jurisdiction}${originalApplicationNumber}${kind}`
        }
      )
      .otherwise(() => {
        // Includes 'IN' - only way to find Indian patents is on the Indian patent office website using this exact original format
        // Includes 'KR' - only way to find Korean patents on the Korean patent office website is using this exact original format
        return defaultBaseApplicationNumber
      })
  )
}
